import { createAsyncThunk } from '@reduxjs/toolkit';
import { TopicsActions } from 'applicaiton/constants/actions';
import { AxiosError } from 'axios';
import { TopicsAPI } from 'integration/api/topics';
import { TopicCreationDto, TopicUpdateDto } from '@docbay/schemas';

/**
 * Fetches topics based on the given parameters.
 *
 * @param data - An object containing the ID and params for fetching topics.
 * @returns A promise that resolves with the fetched topics data, or rejects with an error message.
 */
export const getTopics = createAsyncThunk(
  TopicsActions.getTopics,
  async (
    data: {
      id: string;
      params: {
        page: number;
        limit: number;
        clinicIds?: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await TopicsAPI.getTopics({
        id: data.id,
        params: data.params,
      });
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new topic with the given data.
 *
 * @param params - An object containing the ID and data for creating a new topic.
 * @returns A promise that resolves with the created topic data, or rejects with an error message.
 */
export const createTopic = createAsyncThunk(
  TopicsActions.createTopic,
  async (
    params: { id: string; data: TopicCreationDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await TopicsAPI.createTopic(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates an existing topic with the given data.
 *
 * @param params - An object containing the ID, topic ID, and data for updating an existing topic.
 * @returns A promise that resolves with the updated topic data, or rejects with an error message.
 */
export const editTopic = createAsyncThunk(
  TopicsActions.editTopic,
  async (
    params: { id: string; topicId: string; data: TopicUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await TopicsAPI.editTopic(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an existing topic.
 *
 * @param params - An object containing the ID and topic ID for deleting an existing topic.
 * @returns A promise that resolves with the deleted topic data, or rejects with an error message.
 */
export const deleteTopic = createAsyncThunk(
  TopicsActions.deleteTopic,
  async (params: { id: string; topicId: string }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await TopicsAPI.deleteTopic(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
